import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../hooks/useTheme';
import { useAppLocale } from '../../providers/LocaleProvider';
import { localeMirroredNavArrowClass } from '../../utils/localeNavArrow';

export type TagViewerFilterKey = 'all' | 'completed' | 'incomplete';

export interface TagViewerNavFilterBarProps {
  filter: TagViewerFilterKey;
  onFilterChange: (filter: TagViewerFilterKey) => void;
  countAll: number;
  countCompleted: number;
  countIncomplete: number;
  show: boolean;
  /** Paper controls for the simple theme. */
  simple?: boolean;
}

/** Always-visible status filter for the tag viewer. */
export const TagViewerNavFilterBar: React.FC<TagViewerNavFilterBarProps> = ({
  filter,
  onFilterChange,
  countAll,
  countCompleted,
  countIncomplete,
  show,
  simple = false
}) => {
  const { t } = useTranslation('learning');
  const { direction } = useAppLocale();
  const { isDark } = useTheme();
  const filterPillContainerRef = useRef<HTMLDivElement>(null);
  const allPillRef = useRef<HTMLButtonElement>(null);
  const completedPillRef = useRef<HTMLButtonElement>(null);
  const incompletePillRef = useRef<HTMLButtonElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<{ left: number; width: number }>({ left: 0, width: 0 });

  useEffect(() => {
    const activeCount =
      filter === 'all' ? countAll : filter === 'completed' ? countCompleted : countIncomplete;
    if (activeCount > 0 || filter === 'all' || countAll === 0) return;
    onFilterChange('all');
  }, [filter, countAll, countCompleted, countIncomplete, onFilterChange]);

  const indicatorColors: Record<TagViewerFilterKey, string> = {
    all: simple
      ? (isDark ? 'rgb(212 180 138)' : 'rgb(63 42 86)')
      : (isDark ? 'rgb(229 231 235)' : 'rgb(31 41 55)'),
    completed: isDark ? 'rgb(52 211 153)' : 'rgb(5 150 105)',
    incomplete: isDark ? 'rgb(251 191 36)' : 'rgb(245 158 11)'
  };

  const measureIndicator = () => {
    const container = filterPillContainerRef.current;
    const active =
      filter === 'all' ? allPillRef.current : filter === 'completed' ? completedPillRef.current : incompletePillRef.current;
    if (!container || !active) return;
    const cRect = container.getBoundingClientRect();
    const aRect = active.getBoundingClientRect();
    setIndicatorStyle({ left: aRect.left - cRect.left, width: aRect.width });
  };

  useEffect(() => {
    measureIndicator();
    const timer = window.setTimeout(measureIndicator, 50);
    window.addEventListener('resize', measureIndicator);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', measureIndicator);
    };
  }, [filter, countAll, countCompleted, countIncomplete, simple, show]);

  if (!show) return null;

  const trackClass = simple
    ? 'relative inline-flex max-w-full items-center gap-0.5 rounded-full border border-[#e4d5c3] bg-[#fffdf8] p-1 dark:border-[#3d342c] dark:bg-[#1c1714]'
    : 'relative inline-flex max-w-full items-center gap-0.5 rounded-full border border-violet-200/80 bg-white/75 p-1 shadow-sm dark:border-violet-800/45 dark:bg-violet-950/35';

  const idlePillClass = simple
    ? 'text-[#8a6a45] hover:text-[#3f2a56] dark:text-[#d4b48a] dark:hover:text-[#f6efe6]'
    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100';

  const pillClass = (active: boolean, activeText: string) =>
    `relative z-10 inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium transition-colors duration-300 disabled:cursor-not-allowed disabled:opacity-40 sm:px-3 sm:text-sm ${
      active ? activeText : idlePillClass
    }`;

  const countClass = (active: boolean, idle: string) =>
    `min-w-[1.125rem] rounded-full px-1 py-0.5 text-[10px] tabular-nums sm:text-xs ${active ? 'bg-white/25 dark:bg-black/20' : idle}`;

  return (
    <div className="flex justify-center" dir={direction}>
      <div ref={filterPillContainerRef} className={trackClass}>
        <div
          className="pointer-events-none absolute bottom-1 top-1 rounded-full"
          style={{
            left: indicatorStyle.left,
            width: indicatorStyle.width,
            backgroundColor: indicatorColors[filter],
            transition:
              'left 0.35s cubic-bezier(0.4, 0, 0.2, 1), width 0.35s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.35s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        />
        <button
          ref={allPillRef}
          type="button"
          disabled={countAll === 0}
          onClick={() => onFilterChange('all')}
          className={pillClass(filter === 'all', simple ? 'text-[#fff8ee] dark:text-[#2b221b]' : 'text-white dark:text-gray-900')}
        >
          {t('catalogFlow.filter.all')}
          <span className={countClass(filter === 'all', simple ? 'bg-[#f7f1e8] text-[#3f2a56] dark:bg-[#2a241f] dark:text-[#f6efe6]' : 'bg-gray-200 dark:bg-gray-600')}>
            {countAll}
          </span>
        </button>
        <button
          ref={completedPillRef}
          type="button"
          disabled={countCompleted === 0}
          onClick={() => onFilterChange('completed')}
          className={pillClass(filter === 'completed', 'text-white dark:text-gray-900')}
        >
          {t('catalogFlow.filter.passed')}
          <span className={countClass(filter === 'completed', 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200')}>
            {countCompleted}
          </span>
        </button>
        <button
          ref={incompletePillRef}
          type="button"
          disabled={countIncomplete === 0}
          onClick={() => onFilterChange('incomplete')}
          className={pillClass(filter === 'incomplete', 'text-gray-900')}
        >
          <span className="hidden sm:inline">{t('catalogFlow.filter.remaining')}</span>
          <span className="sm:hidden">{t('catalogFlow.filter.remainingShort')}</span>
          <span className={countClass(filter === 'incomplete', 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200')}>
            {countIncomplete}
          </span>
        </button>
      </div>
    </div>
  );
};

/** Previous / next, matching the lesson viewer's lesson pager, placed at the bottom of the tag viewer. */
export const TagViewerPager: React.FC<{
  displayableCount: number;
  safeContentIndex: number;
  onIndexChange: (updater: (prev: number) => number) => void;
  simple?: boolean;
}> = ({ displayableCount, safeContentIndex, onIndexChange, simple = false }) => {
  const { t } = useTranslation('learning');
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const navArrowClass = (size: string) => localeMirroredNavArrowClass(isRtl, size);
  if (displayableCount < 2) return null;

  const buttonClass = simple
    ? 'flex items-center gap-2 rounded-xl border border-[#e4d5c3] bg-[#fffdf8] p-2 text-sm font-medium text-[#3f2a56] transition-colors hover:bg-[#f7f1e8] disabled:cursor-not-allowed disabled:opacity-50 dark:border-[#3d342c] dark:bg-[#1c1714] dark:text-[#f6efe6] dark:hover:bg-[#2a241f]'
    : 'flex items-center gap-2 rounded-xl bg-gray-200 p-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600';

  return (
    <section
      dir={direction}
      className={
        simple
          ? 'overflow-hidden rounded-2xl border border-[#e4d5c3] bg-[#fffdf8] dark:border-[#3d342c] dark:bg-[#1c1714]'
          : 'overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 shadow-sm dark:border-gray-700 dark:bg-gray-800/90'
      }
    >
      <div className="px-4 py-4">
        <div className="mx-auto flex w-full max-w-md items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => onIndexChange((i) => i - 1)}
            disabled={safeContentIndex === 0}
            className={buttonClass}
            aria-label={t('lesson.previousLesson')}
          >
            <ArrowLeft className={navArrowClass('h-5 w-5')} />
            <span>{t('lesson.previousLesson')}</span>
          </button>
          <button
            type="button"
            onClick={() => onIndexChange((i) => i + 1)}
            disabled={safeContentIndex === displayableCount - 1}
            className={buttonClass}
            aria-label={t('lesson.nextLesson')}
          >
            <span>{t('lesson.nextLesson')}</span>
            <ArrowRight className={navArrowClass('h-5 w-5')} />
          </button>
        </div>
      </div>
    </section>
  );
};

/** Slim tag completion meter. Advanced sits in the hero; simple stays a thin line. */
export const TagViewerProgress: React.FC<{
  done: number;
  total: number;
  simple?: boolean;
}> = ({ done, total, simple = false }) => {
  const { t } = useTranslation('learning');
  const { direction } = useAppLocale();
  if (total <= 0) return null;
  const safeDone = Math.min(Math.max(0, done), total);
  const percent = Math.round((safeDone / total) * 100);
  const label = t('catalogFlow.progress.reviewed', {
    done: safeDone,
    total,
    percent,
    defaultValue: `${safeDone} / ${total}`
  });

  if (simple) {
    return (
      <div
        className="px-1"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={safeDone}
        aria-label={label}
      >
        <div className="mb-1 flex items-center justify-between text-[11px] tabular-nums text-[#8a6a45] dark:text-[#d4b48a]">
          <span>{safeDone}/{total}</span>
          <span>{percent}%</span>
        </div>
        <div className="h-1 overflow-hidden rounded-full bg-[#e4d5c3] dark:bg-[#3d342c]" dir={direction}>
          <div
            className="h-full rounded-full bg-[#3f2a56] transition-[width] duration-500 ease-out motion-reduce:transition-none dark:bg-[#d4b48a]"
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full"
      dir={direction}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={safeDone}
      aria-label={label}
    >
      <div className="mb-1 flex items-center justify-between gap-2 text-[11px] font-semibold tabular-nums text-violet-900/80 dark:text-violet-100/85">
        <span>{safeDone}/{total}</span>
        <span>{percent}%</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/75 shadow-inner dark:bg-black/25">
        <div
          className="h-full rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-emerald-500 transition-[width] duration-500 ease-out motion-reduce:transition-none"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
};
